import { getClientEnvironment, Environment } from '@vp/digital-environment-lib';
import { useLocale } from '@vp/digital-locale-lib';
import { RetryPolicy, HttpClientDispenser } from '@vp/digital-http-client-lib';
import { getLibraryLoggingAttributes, getLogger } from '@vp/digital-logging-lib';
import { getMpvIdMpv } from '@vp/digital-merchandising-site-experience-lib';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var prodConfig = {
    addToCartOrigin: 'https://merchandising.digital.vistaprint.com',
    vistaCartOrigin: 'https://vistacart.orders.vpsvc.com',
    subscriptionCartOrigin: 'https://subscription-cart-service.orders.vpsvc.com',
    cartTenantOrigin: 'https://site-tenant-configuration.s3.eu-west-1.amazonaws.com',
};
var stagingConfig = {
    addToCartOrigin: 'https://qa.merchandising.digital.vistaprint.com',
    vistaCartOrigin: 'https://vistacart.orders.vpsvc.com',
    subscriptionCartOrigin: 'https://subscription-cart-service-staging.orders.vpsvc.com',
    cartTenantOrigin: 'https://site-tenant-configuration.s3.eu-west-1.amazonaws.com',
};
var localConfig = {
    addToCartOrigin: 'https://qa.merchandising.digital.vistaprint.com',
    vistaCartOrigin: 'https://vistacart.orders.vpsvc.com',
    subscriptionCartOrigin: 'https://subscription-cart-service-staging.orders.vpsvc.com',
    cartTenantOrigin: 'https://site-tenant-configuration.s3.eu-west-1.amazonaws.com',
};

function getClientConfig() {
    var env = getClientEnvironment();
    switch (env) {
        case Environment.Prod:
            return prodConfig;
        case Environment.Staging:
            return stagingConfig;
        case Environment.Local:
            return localConfig;
    }
    throw new Error('Cannot determine which environment to use');
}

var getDefaultConfig = function (config) { return ({
    AddToCartApi: {
        baseUrl: config.addToCartOrigin,
        operations: {
            addToCart: {
                timeoutMS: 10 * 1000,
                retry: {
                    policy: RetryPolicy.LinearBackOff,
                    count: 1,
                },
                cache: {
                    maxAgeMS: 60 * 1000,
                    maxSize: 1000,
                },
                parameters: {
                    requestor: 'Digital Merchandising',
                },
            },
        },
    },
    VistaCartApi: {
        baseUrl: config.vistaCartOrigin,
        operations: {
            getCartTemplate: {
                timeoutMS: 4 * 1000,
                retry: {
                    policy: RetryPolicy.LinearBackOff,
                    count: 1,
                },
                cache: {
                    maxAgeMS: 60 * 1000,
                    maxSize: 1000,
                },
                parameters: {
                    requestor: 'Digital Merchandising',
                },
            },
            addProductToCart: {
                timeoutMS: 10 * 1000,
                retry: {
                    policy: RetryPolicy.LinearBackOff,
                    count: 1,
                },
                cache: {
                    maxAgeMS: 60 * 1000,
                    maxSize: 1000,
                },
                parameters: {
                    requestor: 'Digital Merchandising',
                },
            },
        },
    },
    SubscriptionCartApi: {
        baseUrl: config.subscriptionCartOrigin,
        operations: {
            addSubscriptionToCart: {
                timeoutMS: 10 * 1000,
                retry: {
                    policy: RetryPolicy.LinearBackOff,
                    count: 1,
                },
                cache: {
                    maxAgeMS: 60 * 1000,
                    maxSize: 1000,
                },
                parameters: {
                    requestor: 'Digital Merchandising',
                },
            },
        },
    },
    CartTenantApi: {
        baseUrl: config.cartTenantOrigin,
        operations: {
            getCartTenantDetails: {
                timeoutMS: 5 * 1000,
                retry: {
                    policy: RetryPolicy.LinearBackOff,
                    count: 1,
                },
                cache: {
                    maxAgeMS: 60 * 1000,
                    maxSize: 1000,
                },
                parameters: {
                    requestor: 'Digital Merchandising',
                },
            },
        },
    },
}); };

var name = "@vp/digital-cart-lib";
var version = "0.19.21";

var getLibLogger = function (attributes) {
    var attr = getLibraryLoggingAttributes(name, version, attributes);
    return getLogger(attr);
};

function getCartTenantEnvironment() {
    var env = getClientEnvironment();
    if (env === Environment.Prod) {
        return 'production';
    }
    return 'staging';
}

var addToCartLogger$3 = getLibLogger();
var config$4 = getClientConfig();
var apiConfig$4 = getDefaultConfig(config$4).AddToCartApi;
var opConfig$4 = apiConfig$4.operations.addToCart;
var cartClientDispenser = new HttpClientDispenser(apiConfig$4, opConfig$4, true);
var DEFAULT_CART_PATH = '/c';
function addToCart(parameters, cartTemplatePath) {
    return __awaiter(this, void 0, void 0, function () {
        var axiosConfig, cartPath, CART_ORIGIN, countryCode, temp, err_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    addToCartLogger$3.debug('addToCart: calling with parameters ', parameters);
                    axiosConfig = {
                        url: cartTemplatePath,
                        method: 'POST',
                        params: opConfig$4.parameters,
                        data: parameters,
                    };
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4, cartClientDispenser
                            .getInstance()
                            .request(axiosConfig)];
                case 2:
                    cartPath = (_a.sent()).data.cartPath;
                    if (!cartPath) {
                        addToCartLogger$3.info('addToCart: did not error but did not return a path.', parameters);
                        if (getCartTenantEnvironment() === 'staging') {
                            CART_ORIGIN = 'https://cartui.cdn.orders.vpsvc.com';
                            countryCode = useLocale().countryCode;
                            temp = "".concat(CART_ORIGIN, "/VP-").concat(countryCode);
                            temp += '-STAGING';
                            temp += "/".concat(parameters.locale);
                            temp += '/cart';
                            return [2, temp];
                        }
                        return [2, DEFAULT_CART_PATH];
                    }
                    return [2, cartPath];
                case 3:
                    err_1 = _a.sent();
                    addToCartLogger$3.error('addToCart: returned an error', __assign({ response: err_1.response, err: err_1 }, parameters));
                    throw err_1;
                case 4: return [2];
            }
        });
    });
}

var cartLibLogger$2 = getLibLogger();
var CREATE_CART_TEMPLATE_PATH_WEBSITES = '/vistacart-proxy/vistacart/addWebsites';
var CREATE_CART_TEMPLATE_PATH = '/vistacart-proxy/vistacart/add';
function addMcpCompositeKeyToCartClient(productKey, productAttributes, quantity, locale, analytics, customOrderDataFulfillment, productDescriptor) {
    return __awaiter(this, void 0, void 0, function () {
        var parameters, path;
        return __generator(this, function (_a) {
            parameters = {
                productKey: productKey,
                selectedAttributes: productAttributes,
                quantity: quantity,
                locale: locale,
                analytics: analytics,
                customOrderDataFulfillment: customOrderDataFulfillment,
                productDescriptor: productDescriptor,
            };
            cartLibLogger$2.info('addMcpCompositeKeyToCartClient: calling with parameters', parameters);
            path = addToCart(parameters, CREATE_CART_TEMPLATE_PATH_WEBSITES);
            return [2, path];
        });
    });
}
function addSELMToCartClient(digitalIdentifier, quantity, locale, analytics) {
    if (digitalIdentifier === void 0) { digitalIdentifier = 'local_listings_annual'; }
    return __awaiter(this, void 0, void 0, function () {
        var parameters, path;
        return __generator(this, function (_a) {
            parameters = {
                digitalIdentifier: digitalIdentifier,
                quantity: quantity,
                locale: locale,
                analytics: analytics,
            };
            cartLibLogger$2.info('addSELMToCartClient: calling with parameters', parameters);
            path = addToCart(parameters, CREATE_CART_TEMPLATE_PATH);
            return [2, path];
        });
    });
}

var REGEX = /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}|00000000-0000-0000-0000-000000000000)$/i;

function validate(uuid) {
  return typeof uuid === 'string' && REGEX.test(uuid);
}

var VISTAPRINT_BRAND = 'vistaprint';
var cartLibLogger$1 = getLibLogger();
var config$3 = getClientConfig();
var apiConfig$3 = getDefaultConfig(config$3).CartTenantApi;
var opConfig$3 = apiConfig$3.operations.getCartTenantDetails;
var vistacartClientDispenser$1 = new HttpClientDispenser(apiConfig$3, opConfig$3);
function getCartTenantDetails(locale, brand, environment) {
    if (brand === void 0) { brand = VISTAPRINT_BRAND; }
    if (environment === void 0) { environment = 'production'; }
    return __awaiter(this, void 0, void 0, function () {
        var cartTenantPath, axiosConfig, result, err_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    cartLibLogger$1.debug('calling getCartTemplate with parameters', {
                        locale: locale,
                        brand: brand,
                        environment: environment,
                    });
                    cartTenantPath = "/index/".concat(environment, "/").concat(brand, "/").concat(locale);
                    axiosConfig = {
                        url: cartTenantPath,
                        method: 'GET',
                        params: opConfig$3.parameters,
                    };
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4, vistacartClientDispenser$1
                            .getInstance()
                            .request(axiosConfig)];
                case 2:
                    result = (_a.sent()).data;
                    if (!result) {
                        cartLibLogger$1.error('getCartTenantDetails returned invalid result', {
                            locale: locale,
                            brand: brand,
                            environment: environment,
                        });
                        throw new Error('getCartTenantDetails: invalid result returned');
                    }
                    return [2, result];
                case 3:
                    err_1 = _a.sent();
                    cartLibLogger$1.error('getCartTenantDetails returned an error', __assign({ response: err_1.response, err: err_1 }, { locale: locale, brand: brand, environment: environment }));
                    throw err_1;
                case 4: return [2];
            }
        });
    });
}

var PACKAGE_LEVEL = 'Package Level';
function buildCartProductTemplateModel(selectedAttributes, mpv, quantity, analytics, fulfillmentData, uxModData, productDescriptor, imagePath, productName, cartProductDisplayName) {
    if (!imagePath) {
        imagePath = mpv.defaultProductImage;
    }
    var cartProduct = getSubscriptionCartProduct(selectedAttributes, mpv, productName, cartProductDisplayName);
    var subscriptionCartProductTemplateItem = {
        quantity: quantity,
        product: cartProduct,
        design: {
            livePreviewUrl: imagePath,
        },
    };
    if (fulfillmentData || productDescriptor || analytics || uxModData) {
        var customOrderData = {};
        if (fulfillmentData) {
            customOrderData.fulfillment = fulfillmentData;
        }
        if (productDescriptor) {
            customOrderData.modData = { productDescriptor: productDescriptor };
        }
        if (analytics) {
            customOrderData.analytics = analytics;
        }
        if (uxModData) {
            customOrderData.ux = uxModData;
        }
        subscriptionCartProductTemplateItem.customOrderData = customOrderData;
    }
    var cartProductTemplateModel = {
        items: [subscriptionCartProductTemplateItem],
    };
    return cartProductTemplateModel;
}
function getSubscriptionCartProduct(selectedAttributes, mpv, productName, cartProductDisplayName) {
    var _a;
    var productDisplayName = mpv.name;
    var translatedProductAttributes = getTranslatedProductAttributes(selectedAttributes, mpv);
    var planName = (_a = translatedProductAttributes === null || translatedProductAttributes === void 0 ? void 0 : translatedProductAttributes.find(function (productAttribute) { return (productAttribute === null || productAttribute === void 0 ? void 0 : productAttribute.key) === PACKAGE_LEVEL; })) === null || _a === void 0 ? void 0 : _a.valueDisplayName;
    if (cartProductDisplayName) {
        productDisplayName = cartProductDisplayName;
    }
    else if (planName) {
        productDisplayName = "".concat(productName || mpv.name, " - ").concat(planName);
    }
    var cartProduct = {
        productDisplayName: productDisplayName,
        productKey: mpv.coreProductId,
        selectedAttributes: translatedProductAttributes,
    };
    return cartProduct;
}
function getTranslatedProductAttributes(selectedAttributes, mpv) {
    var translatedProductAttributes = Object.keys(selectedAttributes).map(function (key) {
        var merchandisingDetail = mpv.experienceAndOrderingDetails.find(function (detail) { return detail.key.key === key; });
        var merchandisingDetailValues = merchandisingDetail.value.find(function (val) {
            return val.key === selectedAttributes[key];
        });
        var translatedProductAttribute = {
            key: key,
            value: selectedAttributes[key],
            keyDisplayName: merchandisingDetail.key.merchandisingName,
            valueDisplayName: merchandisingDetailValues.merchandisingName,
        };
        return translatedProductAttribute;
    });
    return translatedProductAttributes;
}

var addToCartLogger$2 = getLibLogger();
var config$2 = getClientConfig();
var apiConfig$2 = getDefaultConfig(config$2).SubscriptionCartApi;
var opConfig$2 = apiConfig$2.operations.addSubscriptionToCart;
var subscriptionCartClientDispenser = new HttpClientDispenser(apiConfig$2, opConfig$2, true);
function addSubscriptionToCart(data, digitalTenant, shopperId) {
    return __awaiter(this, void 0, void 0, function () {
        var path, axiosConfig, err_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    path = "api/v1/tenants/".concat(digitalTenant, "/shoppers/").concat(shopperId, "/addToCart");
                    addToCartLogger$2.info('addSubscriptionToCart: calling with parameters ', {
                        path: path,
                        data: data,
                    });
                    axiosConfig = {
                        url: path,
                        method: 'POST',
                        params: opConfig$2.parameters,
                        data: data,
                    };
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4, subscriptionCartClientDispenser.getInstance().request(axiosConfig)];
                case 2:
                    _a.sent();
                    return [3, 4];
                case 3:
                    err_1 = _a.sent();
                    addToCartLogger$2.error('addSubscriptionToCart: returned an error', {
                        response: err_1.response,
                        err: err_1,
                        path: path,
                        data: data,
                    });
                    throw err_1;
                case 4: return [2];
            }
        });
    });
}

var addToCartLogger$1 = getLibLogger();
var VISTAPRINT_TENANT$1 = 'vistaprint';
function addSubscriptionProductToCart(locale, shopperId, params, productName) {
    var _a;
    return __awaiter(this, void 0, void 0, function () {
        var siteId, mpvList, mpv, warnMessage, cartEnv, vpTenantDetails, warnMessage, cartProductTemplateModel;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    siteId = ((_a = params === null || params === void 0 ? void 0 : params.fulfillmentData) === null || _a === void 0 ? void 0 : _a.provisioningSetupProperties) &&
                        JSON.parse(params.fulfillmentData.provisioningSetupProperties).siteId;
                    if (siteId) {
                        if (!validate(siteId)) {
                            addToCartLogger$1.error('addSubscriptionProductToCart: bad siteId', __assign({ locale: locale, shopperId: shopperId }, params));
                        }
                        else {
                            addToCartLogger$1.info('addSubscriptionProductToCart 0/4: good siteId', __assign({ locale: locale, shopperId: shopperId }, params));
                        }
                    }
                    addToCartLogger$1.info('addSubscriptionProductToCart 1/4: calling with parameters ', __assign({ locale: locale, shopperId: shopperId }, params));
                    return [4, getMpvIdMpv(params.mpvId, [locale], addToCartLogger$1)];
                case 1:
                    mpvList = _b.sent();
                    mpv = mpvList[locale];
                    if (!mpv) {
                        warnMessage = 'addSubscriptionProductToCart: Could not retrieve mpv data.';
                        addToCartLogger$1.error(warnMessage, __assign({ locale: locale, shopperId: shopperId }, params));
                        throw new Error(warnMessage);
                    }
                    cartEnv = getCartTenantEnvironment();
                    return [4, getCartTenantDetails(locale, VISTAPRINT_TENANT$1, cartEnv)];
                case 2:
                    vpTenantDetails = _b.sent();
                    if (!vpTenantDetails) {
                        warnMessage = 'addSubscriptionProductToCart: Could not Vistaprint tenant details.';
                        addToCartLogger$1.error(warnMessage, __assign({ locale: locale, shopperId: shopperId, cartEnv: cartEnv }, params));
                        throw new Error(warnMessage);
                    }
                    addToCartLogger$1.info('addSubscriptionProductToCart 2/4: vpTenantDetails ', __assign({ locale: locale, shopperId: shopperId }, vpTenantDetails));
                    cartProductTemplateModel = buildCartProductTemplateModel(params.selectedAttributes, mpv, params.quantity, params.analytics, params.fulfillmentData, params.ux, params.productDescriptor, params.imagePath, productName, params === null || params === void 0 ? void 0 : params.cartProductDisplayName);
                    addToCartLogger$1.info('cartProductTemplateModel 3/4: vpTenantDetails ', __assign({ locale: locale, shopperId: shopperId }, cartProductTemplateModel));
                    return [4, addSubscriptionToCart(cartProductTemplateModel, vpTenantDetails.tenantId, shopperId)];
                case 3:
                    _b.sent();
                    addToCartLogger$1.info('addSubscriptionProductToCart 4/4: Successfully added to cart!', { locale: locale, shopperId: shopperId, cartProductTemplateModel: cartProductTemplateModel });
                    return [2, vpTenantDetails.links.cart];
            }
        });
    });
}

var ProductAttributeKey;
(function (ProductAttributeKey) {
    ProductAttributeKey["packageLevel"] = "Package Level";
    ProductAttributeKey["serviceLength"] = "Service Length";
    ProductAttributeKey["domainProtection"] = "Domain Protection";
})(ProductAttributeKey || (ProductAttributeKey = {}));

var cartLibLogger = getLibLogger();
var config$1 = getClientConfig();
var apiConfig$1 = getDefaultConfig(config$1).VistaCartApi;
var opConfig$1 = apiConfig$1.operations.getCartTemplate;
var vistacartClientDispenser = new HttpClientDispenser(apiConfig$1, opConfig$1, true);
function getCartTemplate(templateId, tenant, shopperId) {
    return __awaiter(this, void 0, void 0, function () {
        var cartTemplatePath, axiosConfig, result, err_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    cartLibLogger.debug('calling getCartTemplate with parameters', {
                        templateId: templateId,
                        tenant: tenant,
                        shopperId: shopperId,
                    });
                    cartTemplatePath = "/tenants/".concat(tenant, "/shoppers/").concat(shopperId, "/carttemplates/").concat(templateId);
                    axiosConfig = {
                        url: cartTemplatePath,
                        method: 'GET',
                        params: opConfig$1.parameters,
                    };
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4, vistacartClientDispenser
                            .getInstance()
                            .request(axiosConfig)];
                case 2:
                    result = (_a.sent()).data;
                    if (!result) {
                        cartLibLogger.error('getCartTemplate returned invalid result', {
                            templateId: templateId,
                            tenant: tenant,
                            shopperId: shopperId,
                        });
                        throw new Error('getCartTemplate: invalid result returned');
                    }
                    return [2, result];
                case 3:
                    err_1 = _a.sent();
                    cartLibLogger.error('getCartTemplate returned an error', __assign({ response: err_1.response, err: err_1 }, { templateId: templateId, tenant: tenant, shopperId: shopperId }));
                    throw err_1;
                case 4: return [2];
            }
        });
    });
}

var TOWER_TYPE = 'TOWER';
var orderTypeEnum;
(function (orderTypeEnum) {
    orderTypeEnum["None"] = "None";
    orderTypeEnum["TowerDiy"] = "TowerDiy";
    orderTypeEnum["TowerDify"] = "TowerDify";
    orderTypeEnum["TowerDifyRecreation"] = "TowerDifyRecreation";
    orderTypeEnum["WixWebsites"] = "WixWebsites";
    orderTypeEnum["WixDomains"] = "WixDomains";
})(orderTypeEnum || (orderTypeEnum = {}));

var freeSubscriptionSignupCommerceToolsProductKey;
(function (freeSubscriptionSignupCommerceToolsProductKey) {
    freeSubscriptionSignupCommerceToolsProductKey["Digtial"] = "PRD-XAPMI5PX";
    freeSubscriptionSignupCommerceToolsProductKey["Physical"] = "PRD-PFRAMQDK";
})(freeSubscriptionSignupCommerceToolsProductKey || (freeSubscriptionSignupCommerceToolsProductKey = {}));
var merchandisingNetworkFulfiller;
(function (merchandisingNetworkFulfiller) {
    merchandisingNetworkFulfiller["dify"] = "CIM-0NDYWY7Q";
    merchandisingNetworkFulfiller["difyRecreation"] = "CIM-3CT8RE4D";
    merchandisingNetworkFulfiller["diy"] = "CIM-P17ZU5PY";
    merchandisingNetworkFulfiller["wix"] = "CIM-GFCBDW13";
    merchandisingNetworkFulfiller["wixPlus"] = "CIM-V9YYF14G";
    merchandisingNetworkFulfiller["wixDomains"] = "CIM-5P765FG0";
})(merchandisingNetworkFulfiller || (merchandisingNetworkFulfiller = {}));

function processOrder(order, shopperId) {
    return __awaiter(this, void 0, void 0, function () {
        var _i, _a, lineItem, tenant, cartTemplateId, cartTemplate, wix, wixDomains, towerDiy, towerDify, towerdifyRecreation;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!order || !order.lineItems) {
                        return [2, [orderTypeEnum.None, null]];
                    }
                    _i = 0, _a = order.lineItems;
                    _b.label = 1;
                case 1:
                    if (!(_i < _a.length)) return [3, 4];
                    lineItem = _a[_i];
                    if (!(lineItem.productKey ===
                        freeSubscriptionSignupCommerceToolsProductKey.Digtial)) return [3, 3];
                    tenant = lineItem.customOrderData.fulfillment.cartTemplateTenant;
                    cartTemplateId = lineItem.customOrderData.fulfillment.cartTemplateId;
                    return [4, getCartTemplate(cartTemplateId, tenant, shopperId)];
                case 2:
                    cartTemplate = _b.sent();
                    wix = cartTemplate.lineItems.find(function (li) {
                        return li.fulfillmentData.mcpProductId === merchandisingNetworkFulfiller.wix;
                    });
                    if (wix) {
                        return [2, [orderTypeEnum.WixWebsites, wix]];
                    }
                    wixDomains = cartTemplate.lineItems.find(function (li) {
                        return li.fulfillmentData.mcpProductId ===
                            merchandisingNetworkFulfiller.wixDomains;
                    });
                    if (wixDomains) {
                        return [2, [orderTypeEnum.WixDomains, wixDomains]];
                    }
                    towerDiy = cartTemplate.lineItems.find(function (li) {
                        return li.fulfillmentData.mcpProductId === merchandisingNetworkFulfiller.diy;
                    });
                    if (towerDiy) {
                        return [2, [orderTypeEnum.TowerDiy, towerDiy]];
                    }
                    towerDify = cartTemplate.lineItems.find(function (li) {
                        return li.fulfillmentData.mcpProductId === merchandisingNetworkFulfiller.dify;
                    });
                    if (towerDify) {
                        return [2, [orderTypeEnum.TowerDify, towerDify]];
                    }
                    towerdifyRecreation = cartTemplate.lineItems.find(function (li) {
                        return li.fulfillmentData.mcpProductId ===
                            merchandisingNetworkFulfiller.difyRecreation;
                    });
                    if (towerdifyRecreation) {
                        return [2, [orderTypeEnum.TowerDifyRecreation, towerdifyRecreation]];
                    }
                    _b.label = 3;
                case 3:
                    _i++;
                    return [3, 1];
                case 4: return [2, [orderTypeEnum.None, null]];
            }
        });
    });
}

function getWixDataFromOrder(order, shopperId) {
    var _a;
    return __awaiter(this, void 0, void 0, function () {
        var result, _loop_1, _i, _b, lineItem;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    result = {
                        wixPropertiesList: [],
                        wixPlusSiteIds: [],
                        wixDomains: [],
                    };
                    if (!order || !order.lineItems) {
                        return [2, result];
                    }
                    _loop_1 = function (lineItem) {
                        var tenant, cartTemplateId, cartTemplate, billingType, subscriptionType_1, wixSitesOrder, wixPlusSitesOrder, wixDomainsOrder;
                        var _d, _e, _f;
                        return __generator(this, function (_g) {
                            switch (_g.label) {
                                case 0:
                                    if (!(lineItem.productKey ===
                                        freeSubscriptionSignupCommerceToolsProductKey.Digtial)) return [3, 2];
                                    tenant = lineItem.customOrderData.fulfillment.cartTemplateTenant;
                                    cartTemplateId = lineItem.customOrderData.fulfillment.cartTemplateId;
                                    return [4, getCartTemplate(cartTemplateId, tenant, shopperId)];
                                case 1:
                                    cartTemplate = _g.sent();
                                    billingType = (_a = lineItem.customOrderData.payment) === null || _a === void 0 ? void 0 : _a.templateBillingDisplayType;
                                    subscriptionType_1 = billingType === 'annualSubscription'
                                        ? 'Yearly'
                                        : billingType === 'monthlySubscription'
                                            ? 'Monthly'
                                            : 'Unknown';
                                    wixSitesOrder = cartTemplate.lineItems
                                        .filter(function (li) {
                                        return li.fulfillmentData.mcpProductId ===
                                            merchandisingNetworkFulfiller.wix;
                                    })
                                        .flatMap(function (wixOrder) {
                                        try {
                                            var parsedProperty = JSON.parse(wixOrder.customOrderData.fulfillment.provisioningSetupProperties);
                                            var siteId = parsedProperty === null || parsedProperty === void 0 ? void 0 : parsedProperty.siteId;
                                            if (!siteId) {
                                                console.warn('Missing siteId in provisioningSetupProperties:', parsedProperty);
                                                return [];
                                            }
                                            return [{ siteId: siteId, subscriptionType: subscriptionType_1 }];
                                        }
                                        catch (error) {
                                            console.error('Failed to parse provisioningSetupProperties:', error);
                                            return [];
                                        }
                                    });
                                    (_d = result.wixPropertiesList).push.apply(_d, wixSitesOrder);
                                    wixPlusSitesOrder = cartTemplate.lineItems
                                        .filter(function (li) {
                                        return li.fulfillmentData.mcpProductId ===
                                            merchandisingNetworkFulfiller.wixPlus;
                                    })
                                        .reduce(function (a, c) { return a.concat(c); }, [])
                                        .map(function (wixOrder) {
                                        return JSON.parse(wixOrder.customOrderData.fulfillment.provisioningSetupProperties).siteId;
                                    });
                                    (_e = result.wixPlusSiteIds).push.apply(_e, wixPlusSitesOrder);
                                    wixDomainsOrder = cartTemplate.lineItems
                                        .filter(function (li) {
                                        return li.fulfillmentData.mcpProductId ===
                                            merchandisingNetworkFulfiller.wixDomains;
                                    })
                                        .reduce(function (a, c) { return a.concat(c); }, [])
                                        .map(function (wixOrder) { return wixOrder.productData.productDisplayName; });
                                    (_f = result.wixDomains).push.apply(_f, wixDomainsOrder);
                                    _g.label = 2;
                                case 2: return [2];
                            }
                        });
                    };
                    _i = 0, _b = order.lineItems;
                    _c.label = 1;
                case 1:
                    if (!(_i < _b.length)) return [3, 4];
                    lineItem = _b[_i];
                    return [5, _loop_1(lineItem)];
                case 2:
                    _c.sent();
                    _c.label = 3;
                case 3:
                    _i++;
                    return [3, 1];
                case 4: return [2, result];
            }
        });
    });
}

var addToCartLogger = getLibLogger();
var config = getClientConfig();
var apiConfig = getDefaultConfig(config).VistaCartApi;
var opConfig = apiConfig.operations.addProductToCart;
var vistaCartClientDispenser = new HttpClientDispenser(apiConfig, opConfig, true);
var VISTAPRINT_TENANT = 'vistaprint';
function addProductToCart(params, locale, shopperId, productName) {
    return __awaiter(this, void 0, void 0, function () {
        var mpvList, mpv, warnMessage, cartEnv, vpTenantDetails, path, productItem, data, axiosConfig, err_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4, getMpvIdMpv(params.mpvId, [locale], addToCartLogger)];
                case 1:
                    mpvList = _a.sent();
                    mpv = mpvList[locale];
                    if (!mpv) {
                        warnMessage = 'addProductToCart: Could not retrieve mpv data.';
                        addToCartLogger.error(warnMessage, __assign({ locale: locale, shopperId: shopperId }, params));
                        throw new Error(warnMessage);
                    }
                    cartEnv = getCartTenantEnvironment();
                    return [4, getCartTenantDetails(locale, VISTAPRINT_TENANT, cartEnv)];
                case 2:
                    vpTenantDetails = _a.sent();
                    path = "tenants/".concat(vpTenantDetails.tenantId, "/carts/").concat(shopperId, "/items");
                    productItem = {
                        product: {
                            productKey: mpv.coreProductId,
                            productDisplayName: productName,
                            selectedAttributes: [],
                        },
                        design: {
                            livePreviewUrl: params.imagePath,
                        },
                        quantity: params.quantity,
                        customOrderData: params.ux ? { ux: params.ux } : undefined,
                    };
                    data = {
                        items: [productItem],
                    };
                    addToCartLogger.info('addProductToCart: calling with parameters ', {
                        path: path,
                        data: data,
                    });
                    axiosConfig = {
                        url: path,
                        method: 'POST',
                        params: opConfig.parameters,
                        data: data,
                    };
                    _a.label = 3;
                case 3:
                    _a.trys.push([3, 5, , 6]);
                    return [4, vistaCartClientDispenser.getInstance().request(axiosConfig)];
                case 4:
                    _a.sent();
                    return [2, vpTenantDetails.links.cart];
                case 5:
                    err_1 = _a.sent();
                    addToCartLogger.error('addToCart: returned an error', {
                        response: err_1.response,
                        err: err_1,
                        path: path,
                        data: data,
                    });
                    throw err_1;
                case 6: return [2];
            }
        });
    });
}

export { ProductAttributeKey, TOWER_TYPE, addMcpCompositeKeyToCartClient, addProductToCart, addSELMToCartClient, addSubscriptionProductToCart, getCartTemplate, getCartTenantDetails, getWixDataFromOrder, orderTypeEnum, processOrder };
